import React, { useMemo, useState } from "react";
import { BASE_URL, ensureArray, getApiHeaders } from "./config";
import { getAuthToken, getAuthUserKey } from "./auth/authUtils";
import { LOGIC, OPERATORS, getQualifiedSymbols } from "./ruleEngine";

const defaultRule = () => ({
  name: "",
  description: "",
  groupLogic: LOGIC.AND,
  groups: [
    {
      timeFrame: 60,
      conditionLogic: LOGIC.AND,
      conditions: [{ field: "", operator: "EQ", value: "" }],
    },
  ],
});

const RuleFilterPanel = ({
  signals,
  enabled,
  onToggleEnabled,
  onQualifiedSymbolsChange,
  allowPresetPersistence = true,
  availableFields = [],
  evaluateRule,
  onEvaluationRows,
}) => {
  const [rule, setRule] = useState(defaultRule());
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [backendQualifiedSymbols, setBackendQualifiedSymbols] = useState([]);
  const [evaluating, setEvaluating] = useState(false);

  // Build field list from data dynamically
  const fieldOptions = useMemo(() => {
    const safeSignals = ensureArray(signals);
    const sample = safeSignals.find(Boolean);
    if (availableFields.length) return availableFields;
    if (!sample) return [];
    const exclude = new Set([
      "symbol",
      "timeFrame",
      "marketPrice",
      "change",
      "id",
      "createdAt",
      "updatedAt",
    ]);
    return Object.keys(sample)
      .filter((k) => !exclude.has(k))
      .sort();
  }, [signals, availableFields]);

  // Compute qualified symbols whenever rule changes
  const locallyQualifiedSymbols = useMemo(() => {
    return getQualifiedSymbols(ensureArray(signals), rule);
  }, [signals, rule]);
  const qualifiedSymbols = evaluateRule ? backendQualifiedSymbols : locallyQualifiedSymbols;

  React.useEffect(() => {
    if (!evaluateRule) return undefined;
    const timer = window.setTimeout(async () => {
      setEvaluating(true);
      try {
        const result = await evaluateRule(rule);
        setBackendQualifiedSymbols(ensureArray(result?.qualifiedSymbols));
        onEvaluationRows?.(ensureArray(result?.matchedSignals));
        setStatusMsg("");
      } catch (error) {
        setBackendQualifiedSymbols([]);
        onEvaluationRows?.([]);
        setStatusMsg(error.message || "Rule evaluation failed");
      } finally { setEvaluating(false); }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [rule, evaluateRule, onEvaluationRows]);

  // Push qualified symbols upward (only when enabled)
  React.useEffect(() => {
    if (enabled) onQualifiedSymbolsChange(qualifiedSymbols, rule);
    else onQualifiedSymbolsChange([], rule); // clear override
  }, [enabled, qualifiedSymbols, onQualifiedSymbolsChange, rule]);

  const updateGroup = (idx, patch) => {
    setRule((prev) => {
      const groups = [...prev.groups];
      groups[idx] = { ...groups[idx], ...patch };
      return { ...prev, groups };
    });
  };

  const updateCondition = (gIdx, cIdx, patch) => {
    setRule((prev) => {
      const groups = [...prev.groups];
      const conds = [...(groups[gIdx].conditions || [])];
      conds[cIdx] = { ...conds[cIdx], ...patch };
      groups[gIdx] = { ...groups[gIdx], conditions: conds };
      return { ...prev, groups };
    });
  };

  const addGroup = () => {
    setRule((prev) => ({
      ...prev,
      groups: [
        ...prev.groups,
        {
          timeFrame: 15,
          conditionLogic: LOGIC.AND,
          conditions: [{ field: "", operator: "EQ", value: "" }],
        },
      ],
    }));
  };

  const removeGroup = (idx) => {
    setRule((prev) => ({
      ...prev,
      groups: prev.groups.filter((_, i) => i !== idx),
    }));
  };

  const addCondition = (gIdx) => {
    setRule((prev) => {
      const groups = [...prev.groups];
      const conds = [...(groups[gIdx].conditions || [])];
      conds.push({ field: "", operator: "EQ", value: "" });
      groups[gIdx] = { ...groups[gIdx], conditions: conds };
      return { ...prev, groups };
    });
  };

  const removeCondition = (gIdx, cIdx) => {
    setRule((prev) => {
      const groups = [...prev.groups];
      const conds = (groups[gIdx].conditions || []).filter((_, i) => i !== cIdx);
      groups[gIdx] = { ...groups[gIdx], conditions: conds.length ? conds : [{ field: "", operator: "EQ", value: "" }] };
      return { ...prev, groups };
    });
  };

  const presetHeaders = (extraHeaders = {}) => {
    const token = getAuthToken();
    const userKey = getAuthUserKey();
    return getApiHeaders({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(userKey ? { "X-SIGNALFLY-USER": userKey } : {}),
      ...extraHeaders,
    });
  };

  const fetchPresets = async () => {
    if (!allowPresetPersistence) return;
    try {
      setStatusMsg("Loading presets...");
      const rulesetUrl = process.env.NODE_ENV === "development" ? "/api/bot/ui/rulesets" : `${BASE_URL}/api/bot/ui/rulesets`;
      const res = await fetch(rulesetUrl, {
        headers: presetHeaders(),
      });
      const data = await res.json();
      setPresets(ensureArray(data));
      setStatusMsg("");
    } catch (e) {
      setStatusMsg("Failed to load presets");
    }
  };

  const loadPreset = () => {
    if (!allowPresetPersistence) return;
    const p = presets.find((x) => String(x.id) === String(selectedPresetId));
    if (!p) return;
    setRule(p.definition);
    setStatusMsg(`Loaded preset: ${p.name}`);
  };

  const savePreset = async () => {
    if (!allowPresetPersistence) return;
    try {
      if (!rule.name?.trim()) {
        setStatusMsg("Rule name is required to save.");
        return;
      }

      setStatusMsg("Saving...");
      const payload = {
        name: rule.name,
        description: rule.description || "",
        isPublic: true,
        definition: rule,
      };

      const rulesetUrl = process.env.NODE_ENV === "development" ? "/api/bot/ui/rulesets" : `${BASE_URL}/api/bot/ui/rulesets`;
      const res = await fetch(rulesetUrl, {
        method: "POST",
        headers: presetHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("save failed");
      setStatusMsg("Saved ✅");
      await fetchPresets();
    } catch (e) {
      setStatusMsg("Save failed");
    }
  };

  const deletePreset = async () => {
    if (!allowPresetPersistence) return;
    try {
      if (!selectedPresetId) return;
      setStatusMsg("Deleting...");
      const rulesetBaseUrl = process.env.NODE_ENV === "development" ? "/api/bot/ui/rulesets" : `${BASE_URL}/api/bot/ui/rulesets`;
      const res = await fetch(`${rulesetBaseUrl}/${selectedPresetId}`, {
        method: "DELETE",
        headers: presetHeaders(),
      });
      if (!res.ok) throw new Error("delete failed");
      setSelectedPresetId("");
      setStatusMsg("Deleted ✅");
      await fetchPresets();
    } catch (e) {
      setStatusMsg("Delete failed");
    }
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: expanded ? "14px 16px" : "0",
        background: "#ffffff",
        border: "1px solid #b9e4ea",
        borderRadius: 8,
        color: "#073344",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: expanded ? "0 0 12px" : "12px 14px",
          background: "transparent",
          border: "0",
          borderBottom: expanded ? "1px solid #ccecef" : "0",
          borderRadius: expanded ? "0" : 8,
          color: "#073344",
          cursor: "pointer",
          boxShadow: "none",
          textAlign: "left",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 24,
              height: 24,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              background: "#dff7f5",
              border: "1px solid #b9e4ea",
              color: "#0f7180",
              fontSize: 16,
              lineHeight: 1,
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.16s ease",
            }}
          >
            ▾
          </span>
          <strong>MTF Signal Fusion Scanner</strong>
        </span>
        <span style={{ color: enabled ? "#0f766e" : "#277986", fontSize: 12, fontWeight: 800 }}>
          {enabled ? `Rules ON · ${qualifiedSymbols.length} symbols` : "Click to expand"}
        </span>
      </button>

      {expanded && (
        <div style={{ paddingTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <strong>Filter Controls</strong>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={() => onToggleEnabled(!enabled)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: enabled ? "1px solid #0f766e" : "1px solid #b9e4ea",
              background: enabled ? "#0f766e" : "#eafdff",
              color: enabled ? "#ffffff" : "#073344",
              cursor: "pointer",
            }}
            title="Apply rule result as global symbol filter"
          >
            {enabled ? "Rules: ON" : "Enable Rules"}
          </button>

          {allowPresetPersistence && (
            <button
              onClick={fetchPresets}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #b9e4ea",
                background: "#eafdff",
                color: "#073344",
                cursor: "pointer",
              }}
            >
              Load Presets
            </button>
          )}
        </div>
      </div>

      {allowPresetPersistence && (
        <>
          {/* preset row */}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select
              value={selectedPresetId}
              onChange={(e) => setSelectedPresetId(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 6,
                border: "1px solid #b9e4ea",
                background: "#eafdff",
                color: "#073344",
                minWidth: 260,
              }}
            >
              <option value="">Select preset</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <button onClick={loadPreset} style={btnStyle()}>
              Apply Preset
            </button>
            <button onClick={deletePreset} style={{ ...btnStyle(), background: "#7f1d1d" }}>
              Delete Preset
            </button>
          </div>

          {/* name/desc */}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <input
              value={rule.name}
              onChange={(e) => setRule((p) => ({ ...p, name: e.target.value }))}
              placeholder="Rule name (for saving)"
              style={inputStyle(260)}
            />
            <input
              value={rule.description}
              onChange={(e) => setRule((p) => ({ ...p, description: e.target.value }))}
              placeholder="Description"
              style={inputStyle(380)}
            />
            <button onClick={savePreset} style={btnStyle()}>
              Save as Preset
            </button>
          </div>
        </>
      )}

      {/* group logic */}
      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
        <span>Between timeframes:</span>
        <select
          value={rule.groupLogic}
          onChange={(e) => setRule((p) => ({ ...p, groupLogic: e.target.value }))}
          style={selectStyle(120)}
        >
          <option value={LOGIC.AND}>AND</option>
          <option value={LOGIC.OR}>OR</option>
        </select>

        <button onClick={addGroup} style={btnStyle()}>
          + Add TimeFrame Group
        </button>

        <span style={{ marginLeft: "auto", color: "#0f7180", fontWeight: 700 }}>
          {evaluating ? "Evaluating on server..." : <>Qualified symbols: <b>{qualifiedSymbols.length}</b></>}
        </span>
      </div>

      {/* groups */}
      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
        {rule.groups.map((g, gIdx) => (
          <div
            key={gIdx}
            style={{
              padding: 10,
              border: "1px solid #b9e4ea",
              borderRadius: 8,
              background: "#f8fdfd",
            }}
          >
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <strong>Group #{gIdx + 1}</strong>

              <span>TimeFrame:</span>
              <input
                type="number"
                value={g.timeFrame}
                onChange={(e) => updateGroup(gIdx, { timeFrame: Number(e.target.value) })}
                style={inputStyle(110)}
              />

              <span>Inside group:</span>
              <select
                value={g.conditionLogic || LOGIC.AND}
                onChange={(e) => updateGroup(gIdx, { conditionLogic: e.target.value })}
                style={selectStyle(120)}
              >
                <option value={LOGIC.AND}>AND</option>
                <option value={LOGIC.OR}>OR</option>
              </select>

              <button onClick={() => addCondition(gIdx)} style={btnStyle()}>
                + Add Condition
              </button>

              <button onClick={() => removeGroup(gIdx)} style={{ ...btnStyle(), background: "#7f1d1d" }}>
                Remove Group
              </button>
            </div>

            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {(g.conditions || []).map((c, cIdx) => (
                <div key={cIdx} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select
                    value={c.field}
                    onChange={(e) => updateCondition(gIdx, cIdx, { field: e.target.value })}
                    style={selectStyle(280)}
                  >
                    <option value="">Select field</option>
                    {fieldOptions.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>

                  <select
                    value={c.operator}
                    onChange={(e) => updateCondition(gIdx, cIdx, { operator: e.target.value })}
                    style={selectStyle(140)}
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  <input
                    value={c.value}
                    onChange={(e) => updateCondition(gIdx, cIdx, { value: e.target.value })}
                    placeholder="Expected value"
                    style={inputStyle(240)}
                  />

                  <button
                    onClick={() => removeCondition(gIdx, cIdx)}
                    style={{ ...btnStyle(), background: "#7f1d1d" }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {statusMsg && <div style={{ marginTop: 10, color: "#0f7180", fontWeight: 700 }}>{statusMsg}</div>}
        </div>
      )}
    </div>
  );
};

const btnStyle = () => ({
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #0f7180",
  background: "#0f7180",
  color: "#fff",
  cursor: "pointer",
});

const selectStyle = (w) => ({
  width: w,
  padding: 6,
  borderRadius: 6,
  border: "1px solid #b9e4ea",
  background: "#eafdff",
  color: "#073344",
});

const inputStyle = (w) => ({
  width: w,
  padding: 6,
  borderRadius: 6,
  border: "1px solid #b9e4ea",
  background: "#eafdff",
  color: "#073344",
});

export default RuleFilterPanel;
